/**
 * board-blocks — the Display Board's content-block vocabulary, shared by the
 * public display (QueueDisplay) and the Journey Designer's board Configuration
 * step. A board's `blocks` compose each queue card top-to-
 * bottom; boards saved before blocks existed fall back to
 * `defaultBoardBlocks(layout)` so legacy boards render unchanged.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { MessageBoard, PublicDisplayQueue } from "./queues-api";
import { publicUrl } from "../../lib/public-url";

import { useT } from "@v0y4ge/i18n-runtime";
export interface BoardBlockRec { uid: string; type: string; cfg: Record<string, unknown> }

/** Paginate a board's queues onto the screen it actually has, and cycle the
 *  pages (2026-09-02, operator).
 *
 *  A lobby board cannot show an unbounded number of queues at a readable size,
 *  and the two things tried before both failed for the same reason: card height
 *  is a FUNCTION of column width, so it cannot be predicted. Adding columns to
 *  fit more queues narrowed the cards, wrapped their block content and roughly
 *  tripled their height; a constant row-height estimate was wrong by 2.8x and
 *  reported a board as fitting while it cropped nine of ten cards.
 *
 *  So nothing here is estimated. The grid reports the height it really has and
 *  one card reports the height it really renders at, both via `offsetHeight`,
 *  which ignores the designer's preview transform. From those two numbers: how
 *  many rows fit, therefore how many queues per page, therefore how many pages
 *  — and the rest cycle, the way every real departure board does. Attach
 *  `gridRef` to the grid and `cardRef` to the FIRST rendered card. */
export function useBoardPager<T>(items: T[], cols: number, gap: number, cycleMs = 10_000) {
  const [gridH, setGridH] = useState(0);
  const [cardH, setCardH] = useState(0);
  const [page, setPage] = useState(0);
  const observe = (set: (n: number) => void) => {
    const ro = { current: null as ResizeObserver | null };
    return (node: HTMLElement | null) => {
      if (ro.current) { ro.current.disconnect(); ro.current = null; }
      if (!node) return;
      const read = () => { if (node.offsetHeight) set(node.offsetHeight); };
      read();
      if (typeof ResizeObserver === "undefined") return;
      const r = new ResizeObserver(read);
      r.observe(node);
      ro.current = r;
    };
  };
  /* eslint-disable react-hooks/exhaustive-deps */
  const gridRef = useCallback(observe(setGridH), []);
  const cardRef = useCallback(observe(setCardH), []);
  /* eslint-enable react-hooks/exhaustive-deps */
  const rows = gridH > 0 && cardH > 0 ? Math.max(1, Math.floor((gridH + gap) / (cardH + gap))) : 0;
  /* Before the first measurement lands, show everything: one honest frame of
     overflow beats a frame that hides queues for a reason we cannot yet know. */
  const perPage = rows > 0 ? Math.max(1, rows * Math.max(1, cols)) : items.length || 1;
  const pages = Math.max(1, Math.ceil(items.length / perPage));
  useEffect(() => { if (page >= pages) setPage(0); }, [pages, page]);
  useEffect(() => {
    if (pages <= 1) return;
    const t = window.setInterval(() => setPage((p) => (p + 1) % pages), cycleMs);
    return () => window.clearInterval(t);
  }, [pages, cycleMs]);
  const start = Math.min(page, pages - 1) * perPage;
  return { visible: items.slice(start, start + perPage), page: Math.min(page, pages - 1), pages, perPage, rows, gridRef, cardRef };
}
export type BoardNameFormat = NonNullable<MessageBoard["nameFormat"]>;

/** The board's standalone public link — what runs on the lobby TV, independent of Studio. */
export const boardPublicUrl = (id: string) => publicUrl(`board/${id}`, { prefix: "public" });

export const BOARD_BLOCKS: { id: string; l: string; lKey: string; blurb: string; multi?: boolean; defaults: Record<string, unknown> }[] = [
  { id: "now-serving", l: "Now serving", lKey: "queues.board.block.now_serving.label", blurb: "The customer being served, big.", defaults: { label: "Now serving" } },
  { id: "called", l: "Please come up", lKey: "queues.board.block.called.label", blurb: "Customers just called forward.", defaults: { label: "Please come up", max: 3 } },
  { id: "up-next", l: "Up next", lKey: "queues.board.block.up_next.label", blurb: "The waiting order, live positions.", defaults: { label: "Up next", max: 6 } },
  { id: "hours", l: "Opening hours", lKey: "queues.board.block.hours.label", blurb: "When this queue runs.", defaults: { prefix: "Open" } },
  { id: "services", l: "Services in queue", lKey: "queues.board.block.services.label", blurb: "Live per-service counts.", defaults: {} },
  { id: "stats", l: "Waiting & est. wait", lKey: "queues.board.block.stats.label", blurb: "Queue totals footer.", defaults: {} },
  { id: "text", l: "Text", lKey: "queues.board.block.text.label", blurb: "A custom message line.", multi: true, defaults: { text: "Thanks for your patience." } },
];

export const boardBlockUid = () => `bb${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`;
const mk = (type: string, extra: Record<string, unknown> = {}): BoardBlockRec =>
  ({ uid: boardBlockUid(), type, cfg: { ...(BOARD_BLOCKS.find((b) => b.id === type)?.defaults ?? {}), ...extra } });

/** Pre-blocks boards render exactly what they used to. */
export function defaultBoardBlocks(layout: MessageBoard["layout"]): BoardBlockRec[] {
  return layout === "full-list"
    ? [mk("now-serving"), mk("called", { max: 8 }), mk("up-next"), mk("hours"), mk("services"), mk("stats")]
    : [mk("now-serving"), mk("called"), mk("hours"), mk("services"), mk("stats")];
}

export interface BoardColors { bg: string; fg: string; soft: string; panel: string; border: string; accent: string; font: string }
export function boardColors(dark: boolean): BoardColors {
  return {
    bg: dark ? "#0F0E1A" : "#F4F5F8",
    fg: dark ? "#FFFFFF" : "#14131F",
    soft: dark ? "rgba(255,255,255,0.55)" : "#5E6675",
    panel: dark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
    border: dark ? "rgba(255,255,255,0.12)" : "#E3E6EC",
    accent: dark ? "#9DB4FF" : "#2D60EB",
    font: "'Inter var', ui-sans-serif, system-ui, sans-serif",
  };
}

const shorten = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
};
export function fmtBoardPerson(p: { name: string; ticket?: string | null }, nameFormat: BoardNameFormat): string {
  return nameFormat === "full" ? p.name
    : nameFormat === "first" ? p.name.trim().split(/\s+/)[0]
    : nameFormat === "ticket" ? (p.ticket || shorten(p.name))
    : shorten(p.name);
}

/** One content block for one queue card. Pure render — no fetching. */
export function BoardBlockNode({ block, q, C, dark, nameFormat, showWait }:
  { block: BoardBlockRec; q: PublicDisplayQueue; C: BoardColors; dark: boolean; nameFormat: BoardNameFormat; showWait: boolean }) {
  const t = useT();
  const cfg = block.cfg as { label?: string; max?: number; prefix?: string; text?: string };
  const secLabel: CSSProperties = { fontSize: 12.5, fontWeight: 700, color: C.soft, textTransform: "uppercase", letterSpacing: 0.08, marginBottom: 8 };
  if (block.type === "now-serving") return (
    <div>
      <div style={{ ...secLabel, color: C.accent, letterSpacing: 0.1, fontSize: 13, marginBottom: 6 }}>{cfg.label || t("queues.boardBlocks.text6", "Now serving")}</div>
      <div style={{ fontSize: 46, fontWeight: 800, lineHeight: 1.1, letterSpacing: -0.5, color: C.fg }}>{q.nowServing ? fmtBoardPerson(q.nowServing, nameFormat) : "—"}</div>
      {q.nowServing?.ticket && nameFormat !== "ticket" ? <div style={{ fontSize: 15, fontWeight: 700, color: C.accent, marginTop: 4, letterSpacing: 0.06 }}>{t("queues.boardBlocks.text2", "Ticket")}{" "}{q.nowServing.ticket}</div> : null}
    </div>
  );
  if (block.type === "called") {
    if (!q.called.length) return null;
    return (
      <div>
        <div style={secLabel}>{cfg.label || t("queues.boardBlocks.text7", "Please come up")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {q.called.slice(0, cfg.max ?? 3).map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 22, fontWeight: 700, color: C.fg }}>
              <span aria-hidden style={{ width: 34, height: 34, borderRadius: 999, background: dark ? "rgba(255,255,255,0.12)" : "#EDF0F6", color: C.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, flexShrink: 0 }}>→</span>
              {fmtBoardPerson(c, nameFormat)}
              {c.ticket && nameFormat !== "ticket" ? <span style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>{c.ticket}</span> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (block.type === "up-next") {
    const list = [...(q.upNext ?? [])].sort((a, b) => a.position - b.position).slice(0, cfg.max ?? 6);
    if (!list.length) return null;
    return (
      <div>
        <div style={secLabel}>{cfg.label || t("queues.boardBlocks.text8", "Up next")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {list.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 17, fontWeight: 600, color: C.soft }}>
              <span aria-label={t("queues.boardBlocks.ariaLabel2", "Position {position} in line", { position: c.position })} style={{ width: 28, height: 28, borderRadius: 999, background: dark ? "rgba(255,255,255,0.08)" : "#EDF0F6", color: C.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{c.position}</span>
              {fmtBoardPerson(c, nameFormat)}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (block.type === "hours") return q.hoursLabel ? <div style={{ fontSize: 13.5, color: C.soft }}>{cfg.prefix || t("queues.boardBlocks.text9", "Open")} {q.hoursLabel}</div> : null;
  if (block.type === "services") {
    if (!q.services?.length) return null;
    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {q.services.map((s) => (
          <span key={s.name} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: 999, border: `1px solid ${C.border}`, background: dark ? "rgba(255,255,255,0.05)" : "#F4F5F8", fontSize: 13.5, fontWeight: 600, color: C.soft }}>
            {s.name}<b style={{ color: C.fg }}>{s.waiting}</b>
          </span>
        ))}
      </div>
    );
  }
  if (block.type === "stats") return (
    <div style={{ display: "flex", gap: 24, fontSize: 16, color: C.soft }}>
      <span><b style={{ color: C.fg, fontSize: 20 }}>{q.waiting}</b> {t("queues.boardBlocks.text3", "waiting")}</span>
      {showWait ? <span>{t("queues.boardBlocks.text4", "about")}{" "}<b style={{ color: C.fg, fontSize: 20 }}>{q.estWaitMins}</b> {t("queues.boardBlocks.text5", "min")}</span> : null}
    </div>
  );
  if (block.type === "text") return cfg.text ? <div style={{ fontSize: 15, color: C.soft, lineHeight: 1.5 }}>{cfg.text}</div> : null;
  return null;
}

/** One queue card: fixed name header + the board's content blocks. `wrap`
 *  lets the builder make each block selectable without forking the renderer. */
export function BoardQueueCard({ q, blocks, C, dark, nameFormat, showWait, wrap }:
  { q: PublicDisplayQueue; blocks: BoardBlockRec[]; C: BoardColors; dark: boolean; nameFormat: BoardNameFormat; showWait: boolean; wrap?: (b: BoardBlockRec, node: ReactNode) => ReactNode }) {
  const stats = blocks.filter((b) => b.type === "stats");
  const rest = blocks.filter((b) => b.type !== "stats");
  const render = (b: BoardBlockRec) => {
    const node = <BoardBlockNode block={b} q={q} C={C} dark={dark} nameFormat={nameFormat} showWait={showWait} />;
    return wrap ? <span key={b.uid} style={{ display: "contents" }}>{wrap(b, node)}</span> : <span key={b.uid} style={{ display: "contents" }}>{node}</span>;
  };
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 20, padding: "26px 28px", display: "flex", flexDirection: "column", gap: 18, fontFamily: C.font }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: C.soft, textTransform: "uppercase", letterSpacing: 0.06 }}>{q.name}</div>
      {rest.map(render)}
      {stats.length ? <div style={{ marginTop: "auto" }}>{stats.map(render)}</div> : null}
    </div>
  );
}
